import { useState } from 'react';
import { X, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';

/** Minimal shape the modal needs from a supplier appeal awaiting response. */
export interface BulkRespondAppeal {
  id: number;
  grn_number?: string;
  po_number?: string;
  part_number?: string;
  on_time_status?: string;
}

interface BulkRespondModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether the customer is accepting or rejecting the selected appeals. */
  action: 'accept' | 'reject';
  /** Supplier appeals (awaiting response) selected for a bulk decision. */
  appeals: BulkRespondAppeal[];
  onSuccess: () => void;
}

/**
 * Customer bulk (multi-select) appeal-response modal.
 *
 * Accepts OR rejects the selected supplier-raised appeals in a single submit.
 * It never creates appeals — only appeals already awaiting a customer response
 * are actioned (the retirement of customer-side appeal creation stays intact).
 * A shared response note applies to all.
 */
export default function BulkRespondModal({ isOpen, onClose, action, appeals, onSuccess }: BulkRespondModalProps) {
  const [responseNote, setResponseNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const isAccept = action === 'accept';
  const appealIds = appeals.map((a) => a.id);
  const count = appealIds.length;

  const Icon = isAccept ? CheckCircle2 : XCircle;
  // Full static class strings — Tailwind purges anything it can't see whole.
  const iconClass = isAccept ? 'text-green-500' : 'text-red-500';
  const badgeClass = isAccept ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  const headingClass = isAccept ? 'text-green-700' : 'text-red-700';
  const focusRingClass = isAccept ? 'focus:ring-green-500' : 'focus:ring-red-500';

  const handleSubmit = async () => {
    if (count === 0) {
      toast.error('No appeals selected');
      return;
    }
    try {
      setSubmitting(true);
      const res = await purchasingAPI.bulkRespondDeliveryAppeals({
        appeal_ids: appealIds,
        action,
        response_note: responseNote || undefined,
      });
      const { processed_count, skipped_count, skipped } = res.data;
      const verb = isAccept ? 'accepted' : 'rejected';
      toast.success(
        skipped_count > 0
          ? `${processed_count} appeals ${verb}, ${skipped_count} skipped`
          : `${processed_count} appeals ${verb}`,
      );
      if (skipped_count > 0 && skipped.length > 0) {
        const reasons = skipped
          .slice(0, 2)
          .map((s) => `#${s.appeal_id}: ${s.reason}`)
          .join('; ');
        const suffix = skipped.length > 2 ? ` (+${skipped.length - 2} more)` : '';
        toast.warning(`Skipped — ${reasons}${suffix}`);
      }
      await Promise.resolve(onSuccess());
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to submit responses'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Icon className={`h-5 w-5 ${iconClass}`} />
              <h3 className="text-lg font-bold">
                {isAccept ? 'Accept' : 'Reject'} Selected Appeals
              </h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                {count} selected
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Explanation */}
          <div className="px-5 pt-4">
            <p className="text-sm text-gray-600">
              {isAccept
                ? 'Accepting applies each supplier appeal — the delivery is re-scored against the claimed Original Due Date.'
                : 'Rejecting keeps each delivery’s original on-time verdict. The supplier is notified of your decision.'}
            </p>
          </div>

          {/* Selected appeal summary */}
          <div className="px-5 pt-4">
            <p className={`text-sm font-semibold ${headingClass} mb-2`}>Selected appeals</p>
            <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
              {appeals.map((a) => (
                <div key={String(a.id)} className="px-3 py-2 text-sm grid grid-cols-4 gap-2">
                  <span className="font-medium truncate" title={a.grn_number}>{a.grn_number || `#${a.id}`}</span>
                  <span className="text-gray-600 truncate" title={a.po_number}>{a.po_number || '-'}</span>
                  <span className="font-mono text-gray-600 truncate" title={a.part_number}>{a.part_number || '-'}</span>
                  <span className="capitalize text-gray-500">{a.on_time_status || '-'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Response note */}
          <div className="p-5 space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Response Note</label>
              <textarea
                value={responseNote}
                onChange={(e) => setResponseNote(e.target.value)}
                placeholder={isAccept ? 'Optional note for the supplier...' : 'Reason for rejection (optional)...'}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${focusRingClass} resize-none`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || count === 0}
              className={isAccept ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
            >
              {submitting ? 'Submitting...' : `${isAccept ? 'Accept' : 'Reject'} ${count} Appeal${count === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
